// PixSora Blog — runtime: theme toggle, reading progress, TOC scrollspy, search modal.
import "../css/index.css";

const root = document.documentElement;

/* ---------- theme toggle ---------- */
function initTheme() {
  const stored = localStorage.getItem("pixsora-theme");
  root.setAttribute("data-theme", stored === "dark" ? "dark" : "light");
  updateThemeButton();

  document.querySelectorAll("[data-theme-toggle]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = root.getAttribute("data-theme") === "dark" ? "light" : "dark";
      root.setAttribute("data-theme", next);
      localStorage.setItem("pixsora-theme", next);
      updateThemeButton();
    });
  });
}

function updateThemeButton() {
  const isDark = root.getAttribute("data-theme") === "dark";
  document.querySelectorAll("[data-theme-toggle]").forEach((btn) => {
    btn.querySelector("[data-icon-sun]")?.toggleAttribute("hidden", !isDark);
    btn.querySelector("[data-icon-moon]")?.toggleAttribute("hidden", isDark);
    btn.setAttribute("aria-pressed", isDark ? "true" : "false");
  });
}

/* ---------- reading progress (article pages) ---------- */
function initReadingProgress() {
  const target = document.querySelector("[data-progress-target]");
  const bar = document.querySelector("[data-progress-bar]");
  if (!target || !bar) return;
  function update() {
    const rect = target.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const scrolled = -rect.top;
    const pct = Math.max(0, Math.min(1, scrolled / Math.max(total, 1)));
    bar.style.transform = `scaleX(${pct})`;
  }
  update();
  window.addEventListener("scroll", update, { passive: true });
  window.addEventListener("resize", update);
}

/* ---------- TOC: generate from h2/h3 in content + scrollspy ---------- */
function initTOC() {
  const tocList = document.querySelector("[data-toc-list]");
  const content = document.querySelector("[data-prose]");
  if (!tocList || !content) return;

  const headings = Array.from(content.querySelectorAll("h2, h3"));
  if (!headings.length) {
    tocList.closest("[data-toc-wrap]")?.setAttribute("hidden", "");
    return;
  }

  headings.forEach((h, i) => {
    if (!h.id) {
      const base = (h.textContent || `section-${i}`)
        .toLowerCase()
        .replace(/[^\wа-яё]+/gi, "-")
        .replace(/-+/g, "-")
        .replace(/(^-|-$)/g, "");
      h.id = base || `section-${i}`;
    }
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.href = "#" + h.id;
    a.textContent = h.textContent || "";
    if (h.tagName === "H3") a.classList.add("lvl-3");
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const el = document.getElementById(h.id);
      if (el) window.scrollTo({ top: el.offsetTop - 96, behavior: "smooth" });
    });
    li.appendChild(a);
    tocList.appendChild(li);
  });

  const links = Array.from(tocList.querySelectorAll("a"));
  function spy() {
    const top = window.scrollY + 140;
    let current = headings[0]?.id;
    for (const h of headings) {
      if (h.offsetTop <= top) current = h.id;
    }
    links.forEach((a) => a.classList.toggle("active", a.getAttribute("href") === "#" + current));
  }
  spy();
  window.addEventListener("scroll", spy, { passive: true });
}

/* ---------- search modal (Cmd+K) ---------- */
function initSearch() {
  const modal = document.querySelector("[data-search-modal]");
  const trigger = document.querySelectorAll("[data-search-trigger]");
  if (!modal) return;
  const input = modal.querySelector("[data-search-input]");
  const list = modal.querySelector("[data-search-results]");
  const sectionTitle = modal.querySelector("[data-search-section-title]");
  const closeBtn = modal.querySelector("[data-search-close]");

  let allItems = [];
  let filtered = [];
  let hi = 0;
  let lastFocused = null;

  function open() {
    lastFocused = document.activeElement;
    modal.removeAttribute("hidden");
    setTimeout(() => input?.focus(), 30);
    render("");
  }
  function close() {
    modal.setAttribute("hidden", "");
    input?.setAttribute("aria-expanded", "false");
    input?.removeAttribute("aria-activedescendant");
    if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
  }

  function render(q) {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) {
      filtered = allItems.slice(0, 6);
      sectionTitle.textContent = "Популярное сейчас";
    } else {
      filtered = allItems
        .map((it) => ({
          it,
          score:
            (it.title.toLowerCase().includes(needle) ? 3 : 0) +
            (it.excerpt.toLowerCase().includes(needle) ? 2 : 0) +
            (it.tags.toLowerCase().includes(needle) ? 2 : 0),
        }))
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
        .map((x) => x.it);
      sectionTitle.textContent = `Найдено: ${filtered.length}`;
    }
    hi = 0;
    list.innerHTML = filtered.length
      ? filtered.map((it, i) => itemMarkup(it, i, needle)).join("")
      : `<div style="padding:32px 16px;text-align:center;color:var(--muted);font-size:14px">Ничего не нашлось. Попробуйте короче.</div>`;
    if (input) {
      input.setAttribute("aria-expanded", filtered.length ? "true" : "false");
      if (filtered.length) input.setAttribute("aria-activedescendant", "search-opt-0");
      else input.removeAttribute("aria-activedescendant");
    }
    bindRows();
  }

  function itemMarkup(it, i, needle) {
    const hl = (text) => needle ? text.replace(new RegExp(`(${needle.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\$&")})`, "ig"), "<mark>$1</mark>") : text;
    return `
      <a class="modal-item${i === 0 ? " hi" : ""}" href="${it.url}" id="search-opt-${i}" role="option" aria-selected="${i === 0 ? "true" : "false"}" tabindex="-1" data-row-idx="${i}">
        <div class="modal-item-thumb">${it.thumb ? `<img src="${it.thumb}" alt="" loading="lazy">` : ""}</div>
        <div style="flex:1;min-width:0">
          <div class="modal-item-title" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${hl(it.title)}</div>
          <div class="modal-item-meta">${it.meta}</div>
        </div>
      </a>`;
  }

  function bindRows() {
    list.querySelectorAll(".modal-item").forEach((row, i) => {
      row.addEventListener("mouseenter", () => setHi(i));
    });
  }

  function setHi(i) {
    hi = Math.max(0, Math.min(filtered.length - 1, i));
    list.querySelectorAll(".modal-item").forEach((row, j) => {
      const on = j === hi;
      row.classList.toggle("hi", on);
      row.setAttribute("aria-selected", on ? "true" : "false");
    });
    const active = list.querySelectorAll(".modal-item")[hi];
    if (active && input) input.setAttribute("aria-activedescendant", active.id);
  }

  // Source items from the data-attribute dataset rendered by Ghost
  const dataEl = document.querySelector("[data-search-source]");
  if (dataEl) {
    allItems = Array.from(dataEl.querySelectorAll("[data-search-item]")).map((el) => ({
      title: el.dataset.title || "",
      excerpt: el.dataset.excerpt || "",
      tags: el.dataset.tags || "",
      url: el.dataset.url || "",
      meta: el.dataset.meta || "",
      thumb: el.dataset.thumb || "",
    }));
  }

  trigger.forEach((t) => t.addEventListener("click", open));
  closeBtn?.addEventListener("click", close);
  modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
  input?.addEventListener("input", (e) => render(e.target.value));

  document.addEventListener("keydown", (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      if (modal.hasAttribute("hidden")) open(); else close();
    }
    if (modal.hasAttribute("hidden")) return;
    if (e.key === "Escape") close();
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(hi + 1); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setHi(hi - 1); }
    if (e.key === "Enter") {
      const row = list.querySelectorAll(".modal-item")[hi];
      if (row) window.location.href = row.getAttribute("href");
    }
    if (e.key === "Tab") {
      const focusable = [input, closeBtn].filter(Boolean);
      if (focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  });
}

/* ---------- sort toggle on home ---------- */
function initSortToggle() {
  const buttons = document.querySelectorAll("[data-sort]");
  const grid = document.querySelector("[data-post-grid]");
  if (!buttons.length || !grid) return;
  const original = Array.from(grid.children);
  let popularOrder = null;

  const apply = (order) => order.forEach((card) => grid.appendChild(card));

  // Reorder the grid by real read counts; 0-view posts keep date order at the end.
  function showPopular() {
    if (popularOrder) { apply(popularOrder); return; }
    const api = blogApiBase();
    if (!api) return;
    fetch(api + "/blog/views/top?limit=50")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        const rank = new Map((data.top || []).map((t, i) => [t.slug, i]));
        const rankOf = (el) =>
          rank.has(el.dataset.slug) ? rank.get(el.dataset.slug) : Infinity;
        popularOrder = [...original].sort(
          (a, b) => rankOf(a) - rankOf(b) || original.indexOf(a) - original.indexOf(b),
        );
        apply(popularOrder);
      })
      .catch(() => {});
  }

  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      buttons.forEach((b) => {
        b.classList.toggle("active", b === btn);
        b.setAttribute("aria-pressed", b === btn ? "true" : "false");
      });
      if (btn.getAttribute("data-sort") === "popular") showPopular();
      else apply(original);
    });
  });
}

/* ---------- mobile burger menu ---------- */
function initNavToggle() {
  const btn = document.querySelector("[data-nav-toggle]");
  const menu = document.querySelector("[data-mobile-menu]");
  if (!btn || !menu) return;
  const iconOpen = btn.querySelector("[data-burger-open]");
  const iconClose = btn.querySelector("[data-burger-close]");
  btn.addEventListener("click", () => {
    const open = menu.classList.toggle("open");
    iconOpen?.toggleAttribute("hidden", open);
    iconClose?.toggleAttribute("hidden", !open);
    btn.setAttribute("aria-expanded", open ? "true" : "false");
  });
}

/* ---------- copy article URL ---------- */
function initShare() {
  document.querySelectorAll("[data-copy-url]").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.preventDefault();
      try {
        await navigator.clipboard.writeText(window.location.href);
        const label = btn.querySelector("[data-share-label]");
        if (label) {
          const prev = label.textContent;
          label.textContent = "Скопировано ✓";
          setTimeout(() => { label.textContent = prev; }, 1500);
        }
      } catch {}
    });
  });
}

/* ---------- blog read counter ---------- */
// Backend base URL: local dev vs production (api.pixsora.ru subdomain).
function blogApiBase() {
  const host = location.hostname;
  return host === "localhost" || host === "127.0.0.1"
    ? "http://localhost:3101"
    : "https://api.pixsora.ru";
}

function pluralReads(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "прочтение";
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return "прочтения";
  return "прочтений";
}

// Post page: count this read once per session, then show the total.
// Articles older than a day get a +300 display boost.
function initViewCounter() {
  const el = document.querySelector("[data-view-count]");
  if (!el) return;
  const api = blogApiBase();
  const slug = el.dataset.slug;
  if (!api || !slug) return;
  const publishedMs = Number(el.dataset.published) || 0;
  const key = "blog-viewed-" + slug;
  let counted = false;
  try { counted = !!sessionStorage.getItem(key); } catch {}
  fetch(api + "/blog/views/" + encodeURIComponent(slug), { method: counted ? "GET" : "POST" })
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((data) => {
      try { sessionStorage.setItem(key, "1"); } catch {}
      const real = Number(data.views) || 0;
      const olderThanDay = publishedMs > 0 && Date.now() - publishedMs > 86400000;
      const shown = olderThanDay ? real + 300 : real;
      el.textContent = shown.toLocaleString("ru-RU") + " " + pluralReads(shown);
      el.hidden = false;
      const dot = document.querySelector("[data-view-dot]");
      if (dot) dot.hidden = false;
    })
    .catch(() => {});
}

// Home "Топ" block: reorder the rendered posts by real read count.
function initTopReorder() {
  const list = document.querySelector("[data-top-list]");
  const api = blogApiBase();
  if (!list || !api) return;
  const items = Array.from(list.querySelectorAll("a[data-slug]"));
  if (items.length < 2) return;
  fetch(api + "/blog/views/top?limit=6")
    .then((r) => (r.ok ? r.json() : Promise.reject()))
    .then((data) => {
      const order = (data.top || []).map((t) => t.slug);
      const ranked = order
        .map((s) => items.find((it) => it.dataset.slug === s))
        .filter(Boolean);
      if (!ranked.length) return;
      const rest = items.filter((it) => !order.includes(it.dataset.slug));
      [...ranked, ...rest].forEach((it) => list.appendChild(it));
      list.querySelectorAll("a[data-slug]").forEach((it, i) => {
        const rank = it.querySelector("[data-top-rank]");
        if (rank && i < 6) rank.textContent = i + 1;
      });
    })
    .catch(() => {});
}

// Home featured block: several featured posts are rendered (first visible,
// rest hidden). Reveal one at random so the hero rotates per page load.
function initFeaturedPick() {
  const picks = document.querySelectorAll("[data-feature-pick]");
  if (picks.length < 2) return;
  const chosen = Math.floor(Math.random() * picks.length);
  picks.forEach((el, i) => { el.hidden = i !== chosen; });
}

initTheme();
initReadingProgress();
initTOC();
initSearch();
initSortToggle();
initShare();
initNavToggle();
initViewCounter();
initTopReorder();
initFeaturedPick();
